import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import PropTypes from "prop-types";
import "chart.js/auto";

const BarChartComponent = ({ cot_summary, selectedYear, onYearChange }) => {
  const jalaliMonths = [
    "فروردین",
    "اردیبهشت",
    "خرداد",
    "تیر",
    "مرداد",
    "شهریور",
    "مهر",
    "آبان",
    "آذر",
    "دی",
    "بهمن",
    "اسفند",
  ];

  const [dataType, setDataType] = useState("value");
  const [totalSum, setTotalSum] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Helper: format large numbers on axis (k / m)
  const formatAxisNumber = (value) => {
    if (value === null || value === undefined) return "";
    const abs = Math.abs(value);
    if (abs >= 1_000_000) {
      const v = (value / 1_000_000).toFixed(1).replace(/\.0$/, "");
      return `${v}m`;
    }
    if (abs >= 1_000) {
      const v = (value / 1_000).toFixed(1).replace(/\.0$/, "");
      return `${v}k`;
    }
    return value.toLocaleString("en-US");
  };

  useEffect(() => {
    if (cot_summary) {
      if (selectedYear === "all" && cot_summary.yearly_data) {
        const sum = cot_summary.yearly_data.reduce(
          (acc, item) => acc + parseFloat(item.total_price),
          0
        );
        const count = cot_summary.yearly_data.reduce(
          (acc, item) => acc + parseInt(item.count, 10),
          0
        );
        setTotalSum(sum);
        setTotalCount(count);
      } else if (cot_summary.monthly_data) {
        const sum = cot_summary.monthly_data.reduce(
          (acc, item) => acc + parseFloat(item.total_price),
          0
        );
        const count = cot_summary.monthly_data.reduce(
          (acc, item) => acc + parseInt(item.count, 10),
          0
        );
        setTotalSum(sum);
        setTotalCount(count);
      } else {
        setTotalSum(0);
        setTotalCount(0);
      }
    }
  }, [cot_summary, selectedYear]);

  const buildChartData = () => {
    if (!cot_summary) return { labels: [], datasets: [] };

    if (selectedYear === "all" && cot_summary.yearly_data) {
      const labels = cot_summary.yearly_data.map((item) => item.year);
      const dataPoints = cot_summary.yearly_data.map((item) =>
        dataType === "value" ? parseFloat(item.total_price) : item.count
      );

      return {
        labels,
        datasets: [
          {
            label:
              dataType === "value"
                ? "مجموع ارزش کوتاژها به تفکیک سال"
                : "تعداد کوتاژها به تفکیک سال",
            data: dataPoints,
            backgroundColor:
              dataType === "value"
                ? "rgba(56, 189, 248, 0.7)" // sky-400
                : "rgba(249, 115, 22, 0.7)", // orange-500
            borderColor:
              dataType === "value"
                ? "rgba(56, 189, 248, 1)"
                : "rgba(249, 115, 22, 1)",
            borderWidth: 1.5,
            borderRadius: 6,
          },
        ],
      };
    }

    if (cot_summary.monthly_data) {
      const monthlyValues = Array(12).fill(0);
      cot_summary.monthly_data.forEach((item) => {
        const idx = item.month - 1;
        if (idx >= 0 && idx < 12) {
          monthlyValues[idx] =
            dataType === "value" ? parseFloat(item.total_price) : item.count;
        }
      });

      return {
        labels: jalaliMonths,
        datasets: [
          {
            label:
              dataType === "value"
                ? `مجموع ارزش کوتاژها در سال ${selectedYear}`
                : `تعداد کوتاژها در سال ${selectedYear}`,
            data: monthlyValues,
            backgroundColor:
              dataType === "value"
                ? "rgba(129, 140, 248, 0.7)" // indigo-400
                : "rgba(59, 130, 246, 0.7)", // blue-500
            borderColor:
              dataType === "value"
                ? "rgba(129, 140, 248, 1)"
                : "rgba(59, 130, 246, 1)",
            borderWidth: 1.5,
            borderRadius: 6,
          },
        ],
      };
    }

    return { labels: [], datasets: [] };
  };

  const chartData = buildChartData();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: { display: false },
      tooltip: {
        backgroundColor: "rgba(15, 23, 42, 0.9)",
        titleFont: { size: 13 },
        bodyFont: { size: 12 },
        padding: 10,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) label += ": ";
            if (context.parsed.y !== null) {
              if (dataType === "value") {
                // FULL value in tooltip
                label += context.parsed.y.toLocaleString("en-US") + " یورو";
              } else {
                label += context.parsed.y.toLocaleString("en-US") + " کوتاژ";
              }
            }
            return label;
          },
        },
      },
      legend: {
        display: true,
        position: "top",
        labels: {
          font: { size: 12 },
          usePointStyle: true,
          padding: 16,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(148, 163, 184, 0.25)",
        },
        ticks: {
          font: { size: 11 },
          callback: function (value) {
            return formatAxisNumber(value);
          },
        },
        title: {
          display: true,
          text: dataType === "value" ? "ارزش (یورو)" : "تعداد کوتاژها",
          font: { size: 13, weight: "600" },
          color: "#334155",
        },
      },
      x: {
        grid: { display: false },
        ticks: {
          font: { size: 11 },
        },
        title: {
          display: true,
          text: selectedYear === "all" ? "سال" : "ماه",
          font: { size: 13, weight: "600" },
          color: "#334155",
        },
      },
    },
  };

  return (
    <div
      dir="rtl"
      className="flex flex-col h-full min-h-[280px] rounded-xl bg-gradient-to-b from-slate-50 to-white"
    >
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-700">
          <span className="font-medium">سال:</span>
          <select
            id="bar-year-selector"
            value={selectedYear}
            onChange={(e) => onYearChange(e.target.value)}
            className="px-2 py-1 rounded-md border border-slate-200 bg-white text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
          >
            <option value="all">همه سال‌ها</option>
            {cot_summary?.yearly_data?.map((item) => (
              <option key={item.year} value={item.year}>
                {item.year}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2 text-xs sm:text-sm">
          <span className="text-slate-600">نمایش:</span>
          <div className="inline-flex rounded-full bg-slate-100 p-1">
            <button
              type="button"
              onClick={() => setDataType("value")}
              className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-all ${
                dataType === "value"
                  ? "bg-emerald-500 text-white shadow-sm"
                  : "text-slate-700 hover:bg-slate-200"
              }`}
            >
              ارزش
            </button>
            <button
              type="button"
              onClick={() => setDataType("count")}
              className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-all ${
                dataType === "count"
                  ? "bg-indigo-500 text-white shadow-sm"
                  : "text-slate-700 hover:bg-slate-200"
              }`}
            >
              تعداد
            </button>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="relative flex-1 min-h-[200px] sm:min-h-[260px] md:min-h-[320px]">
        <div className="absolute inset-0">
          {chartData.labels.length > 0 ? (
            <Bar data={chartData} options={chartOptions} />
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-slate-500">
              داده‌ای برای نمایش وجود ندارد.
            </div>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="mt-3 pt-2 border-t border-slate-100 text-xs sm:text-sm font-semibold text-slate-700 flex items-center justify-between gap-2">
        <span className="truncate">
          {dataType === "value"
            ? `مجموع ارزش کوتاژها: ${totalSum.toLocaleString("en-US")} یورو`
            : `تعداد کوتاژها: ${totalCount.toLocaleString("en-US")} کوتاژ`}
        </span>
        {selectedYear !== "all" && (
          <span className="text-[0.7rem] sm:text-xs text-slate-400">
            سال انتخاب‌شده: {selectedYear}
          </span>
        )}
      </div>
    </div>
  );
};

BarChartComponent.propTypes = {
  cot_summary: PropTypes.shape({
    yearly_data: PropTypes.arrayOf(
      PropTypes.shape({
        year: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        total_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        count: PropTypes.number.isRequired,
      })
    ),
    monthly_data: PropTypes.arrayOf(
      PropTypes.shape({
        month: PropTypes.number.isRequired,
        total_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        count: PropTypes.number.isRequired,
      })
    ),
  }),
  selectedYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onYearChange: PropTypes.func.isRequired,
};

export default BarChartComponent;
